import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, Briefcase, Megaphone } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = "jobs@theotautomations.com";

const REASONS = [
  { icon: Briefcase, title: "Hiring or listing a platform", body: "Want your network, study or micro-task platform on the board? Send the signup link and a short description." },
  { icon: Megaphone, title: "Partnerships & sponsorships", body: "Newsletter placements, featured listings and long-term partnerships." },
  { icon: EnvelopeSimple, title: "Corrections & support", body: "Spotted a dead link, a platform that stopped paying, or an error in a guide? Tell us and we'll fix it." },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll reply by email.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Could not send your message. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#05050A]" data-testid="contact-page">
      <section className="relative pt-28 pb-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="tech-hero-grid" />
        <div className="relative max-w-4xl mx-auto">
          <span className="tag px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full mb-5 inline-block">Contact</span>
          <h1 className="text-4xl sm:text-5xl text-white mb-5" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="contact-title">
            Talk to us
          </h1>
          <p className="text-base md:text-lg text-[#B8B9C3] leading-relaxed mb-6" data-testid="contact-intro">
            Questions about a platform, a listing, or a partnership? Email us directly or use the form below — we read
            everything and usually reply within two business days.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="cta-button inline-flex items-center gap-2 rounded-full px-6 py-3"
            data-testid="contact-email-link"
          >
            <EnvelopeSimple size={18} weight="bold" /> {CONTACT_EMAIL}
          </a>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto grid gap-6 lg:grid-cols-[1.2fr,1fr] items-start">
          <form onSubmit={handleSubmit} className="panel-card p-6 md:p-8 space-y-4" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-[#A1A1AA] text-sm block mb-2">Your name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="bg-[#05050A] border-white/10 text-white"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-[#A1A1AA] text-sm block mb-2">Your email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="bg-[#05050A] border-white/10 text-white"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[#A1A1AA] text-sm block mb-2">Subject</label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                required
                placeholder="Listing request, partnership, correction..."
                className="bg-[#05050A] border-white/10 text-white"
                data-testid="contact-subject-input"
              />
            </div>
            <div>
              <label className="text-[#A1A1AA] text-sm block mb-2">Message</label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                className="bg-[#05050A] border-white/10 text-white min-h-[150px]"
                data-testid="contact-message-input"
              />
            </div>
            <Button type="submit" disabled={sending} className="cta-button rounded-full w-full" data-testid="contact-submit-button">
              {sending ? "Sending..." : (<span className="inline-flex items-center gap-2">Send message <PaperPlaneTilt size={16} weight="bold" /></span>)}
            </Button>
            <p className="text-xs text-[#7A7A85]">
              We only use your email to reply to this message.
            </p>
          </form>

          <div className="grid gap-4">
            {REASONS.map((reason) => (
              <div className="panel-card p-5" key={reason.title} data-testid={`contact-reason-${reason.title.split(" ")[0].toLowerCase()}`}>
                <reason.icon size={22} className="text-[#D9F839] mb-3" />
                <h2 className="text-white text-base mb-1">{reason.title}</h2>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{reason.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
